import React from 'react';
import * as CSS from 'csstype';
import { mix, darken } from 'polished';
import { borders, colors, spaces, typography } from '@99designs/ui/settings';
import styled, { css } from 'styled-components';
import Box from 'libs/core-components/Box/Box';
import Spinner from 'common/Spinner';
import { resolveResponsiveProp, ResponsiveProp } from 'libs/utils/responsiveProps';

export type ButtonVariant = 'primary' | 'secondary';
export type ButtonSize = 'small' | 'medium' | 'large';
export type JoinType = 'start' | 'middle' | 'end';
export type Width = 'auto' | 'fill';

const widths = {
    auto: 'auto',
    fill: '100%',
};

export interface BaseSolidButtonProps {
    variant?: ButtonVariant;
    icon?: React.ReactNode;
    size?: ButtonSize;
    width?: ResponsiveProp<Width> | 'responsive';
    disabled?: boolean;
    processing?: boolean;
    selected?: boolean;
    joined?: JoinType;
    children?: React.ReactNode;
    backgroundColor?: CSS.Property.Color | colors.Color;
}

export function solidButtonStyles({
    variant,
    size = 'medium',
    selected = false,
    processing = false,
    width = 'auto',
    children,
    icon,
    joined,
    backgroundColor,
}: BaseSolidButtonProps) {
    let buttonBackgroundColor: CSS.Property.Color | colors.Color = 'heritage';
    let textColor: CSS.Property.Color | colors.Color = 'white';
    let borderRadius: CSS.Property.BorderRadius<any>;
    let borderWidth: CSS.Property.BorderWidth<any>;
    let fontSize: CSS.Property.FontSize<any>;
    let paddingVertical: CSS.Property.Padding<any>;
    let paddingHorizontal: CSS.Property.Padding<any>;
    let minHeight: CSS.Property.MinHeight<any>;
    let minWidth: CSS.Property.MinWidth<any>;
    let fontFamily: CSS.Property.FontFamily = typography.fontFamilies.text;

    switch (variant) {
        case 'primary':
            buttonBackgroundColor = 'heritage';
            textColor = 'white';
            break;
        case 'secondary':
            buttonBackgroundColor = 't1000';
            textColor = 'puntRoad';
            break;
        default:
            buttonBackgroundColor = backgroundColor ?? 'heritage';
            textColor = 'white';
            break;
    }

    if (selected) {
        buttonBackgroundColor = 'puntRoad';
        textColor = 'bleachedAs';
    }

    switch (size) {
        case 'small':
            borderRadius = `${borders.borderRadiuses.small}px`;
            fontSize = `${typography.typeScale.size2}px`;
            borderWidth = '2px';
            paddingVertical = `2px`;
            paddingHorizontal = `${spaces.space6}px`;
            minHeight = '24px';
            minWidth = '24px';
            break;
        case 'medium':
            borderRadius = `${borders.borderRadiuses.medium}px`;
            fontSize = `${typography.typeScale.size3}px`;
            borderWidth = '2px';
            paddingVertical = `9px`;
            paddingHorizontal = `${spaces.space8}px`;
            minHeight = '44px';
            minWidth = '44px';
            break;
        case 'large':
            borderRadius = `${borders.borderRadiuses.large}px`;
            fontSize = `${typography.typeScale.size4}px`;
            borderWidth = '3px';
            paddingVertical = `12px`;
            paddingHorizontal = `${spaces.gutter}px`;
            minHeight = '56px';
            minWidth = '56px';
            fontFamily = `${typography.fontFamilies.heading}`;
            break;
    }

    if ((icon || processing) && !children) {
        paddingHorizontal = 0;
    }

    const normalizedBackgroundColor = colors.getColor(buttonBackgroundColor);
    const normalizedTextColor = colors.getColor(textColor);
    const hoverBackgroundColor = mix(0.1, 'white', normalizedBackgroundColor);
    const activeBackgroundColor = darken(0.05, colors.getColor(normalizedBackgroundColor));

    const normalizedWidth: ResponsiveProp<Width> =
        width === 'responsive' ? ['fill', 'auto', 'auto'] : width;

    return css`
        display: inline-grid;
        grid-auto-flow: column;
        place-items: center;
        vertical-align: middle;
        margin: 0;
        padding: ${paddingVertical} ${paddingHorizontal};
        line-height: ${typography.lineHeights.typeScale.size3};
        min-height: ${minHeight};
        min-width: ${minWidth};
        text-align: center;
        font-family: ${fontFamily};
        font-size: ${fontSize};

        background: ${normalizedBackgroundColor};
        border-color: ${normalizedBackgroundColor};
        color: ${normalizedTextColor};
        border-width: ${borderWidth};
        border-style: solid;

        -moz-appearance: none;
        -webkit-appearance: none;
        cursor: pointer;
        text-decoration: none;

        border-top-right-radius: ${joined && joined !== 'end' ? 0 : borderRadius};
        border-bottom-right-radius: ${joined && joined !== 'end' ? 0 : borderRadius};
        border-top-left-radius: ${joined && joined !== 'start' ? 0 : borderRadius};
        border-bottom-left-radius: ${joined && joined !== 'start' ? 0 : borderRadius};

        margin-right: ${joined && joined !== 'end' ? '2px' : 0};

        ${resolveResponsiveProp(normalizedWidth, 'width', widths)};

        &:hover,
        &:focus {
            color: ${selected ? textColor : normalizedTextColor};
            background-color: ${selected ? buttonBackgroundColor : hoverBackgroundColor};
            text-decoration: none;
            outline: none;
        }

        &:active {
            background-color: ${activeBackgroundColor};
            border-color: ${activeBackgroundColor};
        }

        &:disabled,
        &[disabled],
        &[aria-disabled='true'] {
            cursor: not-allowed;
            background: ${colors.t1000};
            border-color: ${colors.t1000};
            color: ${colors.pebbleBeach};

            &:hover,
            &:focus,
            &:active {
                background: ${colors.t1000};
                border-color: ${colors.t1000};
                color: ${colors.pebbleBeach};
            }
        }
    `;
}

const StyledButtonContent = styled.div`
    display: inline-grid;
    grid-auto-flow: column;
    place-items: center;
    width: fit-content;
    margin: 0 auto;
`;

interface BaseSolidButtonContentProps {
    icon?: React.ReactNode;
    label: React.ReactNode;
    size?: ButtonSize;
    processing?: boolean;
}

export const BaseSolidButtonContent = ({
    icon = null,
    processing = false,
    label,
    size,
}: BaseSolidButtonContentProps) => {
    let elSpace: spaces.Space = 'space6';
    switch (size) {
        case 'small':
            elSpace = 'space5';
            break;
        case 'medium':
            elSpace = 'space6';
            break;
        case 'large':
            elSpace = 'space7';
            break;
    }
    return (
        <StyledButtonContent>
            {(icon || processing) && (
                <Box display={'flex'} marginRight={label ? elSpace : 'none'}>
                    {processing && <Spinner />}
                    {!processing && icon}
                </Box>
            )}
            {label && <span>{label}</span>}
        </StyledButtonContent>
    );
};
